import React, { useMemo } from 'react';
import {
  buildLast7DayKeys,
  businessDayBreakdown,
  sumBreakdownOverSeries,
  slowStockMoversInWindow,
  topProductsInWindow,
  topWakalaChannelsByTxnCount,
  weeklyStackedSeries,
} from '../utils/dashboardAnalytics';
import { getLocalDateKey } from '../utils/reportDates';
import { channelLabel } from '../wakalaConstants';
import './DashboardAnalytics.css';

const STACK_ORDER = ['pos', 'printing', 'wakala', 'other'];

const SOURCE_META = {
  pos: { label: 'POS', short: 'POS' },
  printing: { label: 'Print', short: 'Print' },
  wakala: { label: 'Wakala', short: 'Wakala' },
  other: { label: 'Other', short: 'Other' },
};

const SLICE_FILL = {
  pos: '#8eb4d4',
  printing: '#b9a3d9',
  wakala: '#8fbfae',
  other: '#d4a896',
};

function niceCeil(max) {
  if (max <= 0) return 1;
  const exp = Math.floor(Math.log10(max));
  const f = max / 10 ** exp;
  let nf = 10;
  if (f <= 1) nf = 1;
  else if (f <= 2) nf = 2;
  else if (f <= 5) nf = 5;
  return nf * 10 ** exp;
}

function formatAxisAmount(v) {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1)}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(v >= 10_000 ? 0 : 1)}k`;
  return Math.round(v).toLocaleString();
}

function formatMoney(v) {
  if (v === 0) return '0';
  const r = Math.round(v);
  if (Math.abs(v - r) < 0.01) return r.toLocaleString();
  return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

/** Donut slice path (angles in radians, 0 = right, clockwise; start from top). */
function donutSlice(cx, cy, rOuter, rInner, a0, a1) {
  if (a1 - a0 < 0.0001) return '';
  const x0 = cx + rOuter * Math.cos(a0);
  const y0 = cy + rOuter * Math.sin(a0);
  const x1 = cx + rOuter * Math.cos(a1);
  const y1 = cy + rOuter * Math.sin(a1);
  const x2 = cx + rInner * Math.cos(a1);
  const y2 = cy + rInner * Math.sin(a1);
  const x3 = cx + rInner * Math.cos(a0);
  const y3 = cy + rInner * Math.sin(a0);
  const large = a1 - a0 > Math.PI ? 1 : 0;
  return `M ${x0} ${y0} A ${rOuter} ${rOuter} 0 ${large} 1 ${x1} ${y1} L ${x2} ${y2} A ${rInner} ${rInner} 0 ${large} 0 ${x3} ${y3} Z`;
}

function DonutChart({ breakdown, caption }) {
  const total = STACK_ORDER.reduce((s, k) => s + (breakdown[k] || 0), 0);
  const cx = 80;
  const cy = 80;
  const rO = 58;
  const rI = 34;

  let angle = -Math.PI / 2;
  const paths = [];
  for (const key of STACK_ORDER) {
    const v = breakdown[key] || 0;
    if (v <= 0 || total <= 0) continue;
    const span = (v / total) * 2 * Math.PI;
    const a0 = angle;
    const a1 = angle + span;
    const d = donutSlice(cx, cy, rO, rI, a0, a1);
    paths.push(
      <path key={key} d={d} fill={SLICE_FILL[key]} stroke="rgba(255,255,255,0.5)" strokeWidth="1.5">
        <title>{`${SOURCE_META[key].label}: ${formatMoney(v)} TZS (${((v / total) * 100).toFixed(0)}%)`}</title>
      </path>
    );
    angle = a1;
  }

  if (total <= 0) {
    return (
      <div className="dash-donut-wrap">
        <span className="dash-donut-caption">{caption}</span>
        <svg className="dash-donut-svg" viewBox="0 0 160 160" aria-hidden="true">
          <circle cx={cx} cy={cy} r={rO} fill="#f1f5f9" stroke="#e2e8f0" />
          <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" className="dash-donut-center-zero">
            —
          </text>
        </svg>
      </div>
    );
  }

  return (
    <div className="dash-donut-wrap">
      <span className="dash-donut-caption">{caption}</span>
      <svg className="dash-donut-svg" viewBox="0 0 160 160" role="img" aria-label={`${caption}: ${formatMoney(total)} TZS total`}>
        <circle cx={cx} cy={cy} r={rO} fill="#f8fafc" />
        {paths}
        <text x={cx} y={cy - 6} textAnchor="middle" className="dash-donut-center-total">
          {formatMoney(total)}
        </text>
        <text x={cx} y={cy + 12} textAnchor="middle" className="dash-donut-center-unit">
          TZS
        </text>
      </svg>
    </div>
  );
}

function DailyTotalsBars({ rows }) {
  const maxScale = useMemo(() => niceCeil(Math.max(1, ...rows.map((r) => r.total))), [rows]);
  /** Wide canvas so 7 bars stay spacious (modern column chart). */
  const w = 560;
  const h = 248;
  const pad = { t: 38, r: 18, b: 44, l: 54 };
  const innerW = w - pad.l - pad.r;
  const innerH = h - pad.t - pad.b;
  const n = rows.length || 1;
  const gap = 14;
  const barW = (innerW - gap * (n - 1)) / n;
  const tickCount = 4;
  const ticks = [];
  for (let i = 0; i <= tickCount; i += 1) {
    const val = (maxScale * i) / tickCount;
    const y = pad.t + innerH - (i / tickCount) * innerH;
    ticks.push({ val, y, i });
  }

  return (
    <div className="dash-bar-chart-frame dash-bar-chart-frame--modern">
      <svg className="dash-analytics-svg dash-bar-chart-svg dash-bar-chart-svg--daily" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMidYMid meet">
        <line className="dash-bar-axis-y" x1={pad.l} y1={pad.t} x2={pad.l} y2={pad.t + innerH} />
        {ticks.map(({ val, y, i }) => (
          <g key={`g-${i}`}>
            <line className="dash-bar-grid" x1={pad.l} y1={y} x2={w - pad.r} y2={y} />
            <text className="dash-bar-y-label" x={pad.l - 6} y={y + (i === 0 ? -2 : i === tickCount ? 10 : 3)} textAnchor="end" dominantBaseline="middle">
              {formatAxisAmount(val)}
            </text>
          </g>
        ))}
        <line className="dash-bar-baseline" x1={pad.l} y1={pad.t + innerH} x2={w - pad.r} y2={pad.t + innerH} />
        {rows.map((row, col) => {
          const x = pad.l + col * (barW + gap);
          const cx = x + barW / 2;
          const ratio = maxScale > 0 ? row.total / maxScale : 0;
          const bh = row.total <= 0 ? 0 : Math.max(4, ratio * innerH);
          const y = pad.t + innerH - bh;
          const labelY = y - 6;
          const showValue = row.total > 0;
          return (
            <g key={row.dateKey}>
              {bh > 0 ? <rect className="dash-daily-bar" x={x} y={y} width={barW} height={bh} rx={2} ry={2} /> : null}
              {showValue ? (
                <text
                  className="dash-bar-data-label"
                  x={cx}
                  y={labelY}
                  textAnchor="middle"
                  dominantBaseline="auto"
                >
                  {formatMoney(row.total)}
                </text>
              ) : null}
              <text className="dash-bar-day-bottom" x={cx} y={h - 14} textAnchor="middle">
                {row.label}
              </text>
            </g>
          );
        })}
      </svg>
      <p className="dash-bar-chart-unit">Scale 0 — {formatAxisAmount(maxScale)} TZS</p>
    </div>
  );
}

export default function DashboardAnalytics({
  salesHistory,
  salesLoading,
  printJobs,
  transactions,
  otherServiceSales = [],
  products = [],
}) {
  const todayKey = getLocalDateKey();
  const dayKeys = useMemo(() => buildLast7DayKeys(), []);

  const stackedRows = useMemo(
    () => weeklyStackedSeries(salesHistory, printJobs, transactions, dayKeys, otherServiceSales),
    [salesHistory, printJobs, transactions, dayKeys, otherServiceSales]
  );

  const weekSums = useMemo(() => sumBreakdownOverSeries(stackedRows), [stackedRows]);
  const todayBreakdown = useMemo(
    () => businessDayBreakdown(salesHistory, printJobs, transactions, todayKey, otherServiceSales),
    [salesHistory, printJobs, transactions, todayKey, otherServiceSales]
  );

  const topProducts = useMemo(() => topProductsInWindow(salesHistory, dayKeys, 5), [salesHistory, dayKeys]);
  const topWakalaLines = useMemo(
    () => topWakalaChannelsByTxnCount(transactions, dayKeys, 5),
    [transactions, dayKeys]
  );
  const slowMovers = useMemo(
    () => slowStockMoversInWindow(salesHistory, products, dayKeys, 5),
    [salesHistory, products, dayKeys]
  );

  const hasAnyChart = stackedRows.some((r) => r.total > 0);
  const hasTopPos = topProducts.some((p) => p.qty > 0);
  const hasTopWakala = topWakalaLines.some((p) => p.qty > 0);
  const hasSlowMovers = slowMovers.length > 0;

  const weekBreakdown = useMemo(
    () => ({
      pos: weekSums.pos,
      printing: weekSums.printing,
      wakala: weekSums.wakala,
      other: weekSums.other,
    }),
    [weekSums]
  );

  return (
    <section className="dash-analytics" aria-label="Analytics">
      <h2 className="dash-analytics-title">Analytics</h2>
      <p className="dash-analytics-lead">Visual split by channel — hover a slice for amounts.</p>

      <div className="dash-analytics-summary">
        <div className="dash-donut-row">
          <DonutChart breakdown={todayBreakdown} caption="Today" />
          <DonutChart breakdown={weekBreakdown} caption="This week" />
        </div>
        <ul className="dash-pie-legend" aria-label="Channels">
          {STACK_ORDER.map((key) => (
            <li key={key}>
              <span className="dash-pie-legend-dot" style={{ background: SLICE_FILL[key] }} aria-hidden="true" />
              {SOURCE_META[key].label}
            </li>
          ))}
        </ul>
      </div>

      <div className="dash-analytics-grid">
        <div className="dash-analytics-card dash-analytics-card--chart">
          <h3 className="dash-analytics-card-title">Last 7 days</h3>
          <p className="dash-analytics-card-hint">Total TZS per day (all channels combined).</p>
          {salesLoading && (!salesHistory || salesHistory.length === 0) ? (
            <p className="dash-analytics-empty">Loading…</p>
          ) : !hasAnyChart ? (
            <p className="dash-analytics-empty">No activity in this window yet.</p>
          ) : (
            <div className="dash-analytics-chart-wrap">
              <DailyTotalsBars rows={stackedRows} />
            </div>
          )}
        </div>

        <div className="dash-analytics-card">
          <h3 className="dash-analytics-card-title">Top movers &amp; slow movers</h3>
          <p className="dash-analytics-card-hint">
            Same 7-day window · fast POS lines and wakala networks; slow list = in-stock products with{' '}
            <strong>zero</strong> POS units sold in that period.
          </p>

          <h4 className="dash-analytics-subblock-title">POS — top movers</h4>
          {!hasTopPos ? (
            <p className="dash-analytics-empty dash-analytics-empty--tight">No POS sales yet.</p>
          ) : (
            <ul className="dash-analytics-top">
              {topProducts.map((p, idx) => {
                const maxQ = topProducts[0]?.qty || 1;
                const pct = Math.max(8, Math.round((p.qty / maxQ) * 100));
                return (
                  <li key={`${p.name}-${idx}`} className="dash-analytics-top-row">
                    <span className="dash-analytics-top-rank">{idx + 1}</span>
                    <div className="dash-analytics-top-body">
                      <div className="dash-analytics-top-name">{p.name}</div>
                      <div className="dash-analytics-top-bar-track" aria-hidden="true">
                        <div className="dash-analytics-top-bar-fill" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    <span className="dash-analytics-top-qty">{p.qty}</span>
                  </li>
                );
              })}
            </ul>
          )}

          <h4 className="dash-analytics-subblock-title">POS — slow movers (in stock, no sales)</h4>
          {!hasSlowMovers ? (
            <p className="dash-analytics-empty dash-analytics-empty--tight">
              No slow movers in this window — every stocked product had at least one POS sale, or inventory is empty.
            </p>
          ) : (
            <ul className="dash-analytics-top">
              {slowMovers.map((p, idx) => {
                const maxS = slowMovers[0]?.stockQty || 1;
                const pct = Math.max(8, Math.round((p.stockQty / maxS) * 100));
                return (
                  <li key={`slow-${p.id}-${idx}`} className="dash-analytics-top-row">
                    <span className="dash-analytics-top-rank dash-analytics-top-rank--slow">{idx + 1}</span>
                    <div className="dash-analytics-top-body">
                      <div className="dash-analytics-top-name" title={p.name}>
                        {p.name}
                      </div>
                      <div className="dash-analytics-top-bar-track dash-analytics-top-bar-track--slow" aria-hidden="true">
                        <div className="dash-analytics-top-bar-fill dash-analytics-top-bar-fill--slow" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    <span className="dash-analytics-top-qty dash-analytics-top-qty--slow" title="Units on hand">
                      {p.stockQty} in stock
                    </span>
                  </li>
                );
              })}
            </ul>
          )}

          <h4 className="dash-analytics-subblock-title">Wakala — line (transactions)</h4>
          {!hasTopWakala ? (
            <p className="dash-analytics-empty dash-analytics-empty--tight">No wakala entries in this week.</p>
          ) : (
            <ul className="dash-analytics-top">
              {topWakalaLines.map((p, idx) => {
                const maxQ = topWakalaLines[0]?.qty || 1;
                const pct = Math.max(8, Math.round((p.qty / maxQ) * 100));
                const lineName = channelLabel(p.channel);
                return (
                  <li key={`${p.channel}-${idx}`} className="dash-analytics-top-row">
                    <span className="dash-analytics-top-rank">{idx + 1}</span>
                    <div className="dash-analytics-top-body">
                      <div className="dash-analytics-top-name">{lineName}</div>
                      <div className="dash-analytics-top-bar-track dash-analytics-top-bar-track--wakala" aria-hidden="true">
                        <div className="dash-analytics-top-bar-fill dash-analytics-top-bar-fill--wakala" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    <span className="dash-analytics-top-qty dash-analytics-top-qty--wakala">{p.qty}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
